'use client'

import { Loader2 } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {
  useClearShopifyTopupBlockMutation,
  useResolveShopifyTopupAttemptMutation,
} from '@/hooks/mutations/shopify-topup-incidents'
import { useShopifyTopupIncidentQuery } from '@/hooks/queries/shopify-topup-incidents'
import { formatCentsAsUsd } from '@/lib/billing/utils/format'
import { formatCredits } from '@/lib/billing/utils/credits'
import type { ShopifyTopupAttemptSummary } from '@/lib/billing/topup/shopify-topup-incident'

interface ShopifyTopupIncidentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tenantId: string
  tenantName: string
  canResolve: boolean
  canClearBlock: boolean
  onChanged: () => void
}

/**
 * Resolves what the settlement sweep could not. Both actions here move real money or lift
 * a billing hold, so each attempt is closed one at a time rather than in bulk.
 */
export function ShopifyTopupIncidentDialog({
  open, onOpenChange, tenantId, tenantName, canResolve, canClearBlock, onChanged,
}: ShopifyTopupIncidentDialogProps) {
  const { data, isLoading, isError, error } = useShopifyTopupIncidentQuery(tenantId, open)
  const resolve = useResolveShopifyTopupAttemptMutation(tenantId, onChanged)
  const clearBlock = useClearShopifyTopupBlockMutation(tenantId, onChanged)
  const busy = resolve.isPending || clearBlock.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[860px]">
        <DialogHeader>
          <DialogTitle><span>Shopify top-ups —</span> {tenantName}</DialogTitle>
          <DialogDescription>
            Look each attempt up by its idempotency key in the App Events log, then record what
            Shopify actually did.
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {isError && (
          <p className="py-6 text-sm text-destructive">
            {error instanceof Error ? error.message : 'Failed to load the incident.'}
          </p>
        )}

        {data && (
          <div className="space-y-4">
            {data.blockedAt ? (
              <Alert variant="destructive">
                <AlertDescription className="space-y-2">
                  <div>
                    <span>Top-ups paused</span> {new Date(data.blockedAt).toLocaleString()} <span>—</span> {data.blockedReason}
                  </div>
                  {canClearBlock && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy || data.escalatedCount > 0}
                      onClick={() => clearBlock.mutate()}
                    >
                      {clearBlock.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                      <span>Lift the hold</span>
                    </Button>
                  )}
                  {data.escalatedCount > 0 && (
                    <div className="text-xs">
                      <span>Close the</span> {data.escalatedCount} <span>escalated attempt(s) first — reconciliation
                      stays blind to their meters until then.</span>
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            ) : (
              <p className="text-sm text-muted-foreground">Top-ups are not blocked for this tenant.</p>
            )}

            {data.unsettled.length === 0 ? (
              <p className="text-sm text-muted-foreground">No unsettled attempts.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Meter</TableHead>
                    <TableHead>Charge</TableHead>
                    <TableHead>Credit owed</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead>Idempotency key</TableHead>
                    {canResolve && <TableHead className="w-[190px]">Resolve</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.unsettled.map((attempt) => (
                    <AttemptRow
                      key={attempt.id}
                      attempt={attempt}
                      canResolve={canResolve}
                      busy={busy}
                      onResolve={(resolution) => resolve.mutate({ attemptId: attempt.id, resolution })}
                    />
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

function AttemptRow({
  attempt, canResolve, busy, onResolve,
}: {
  attempt: ShopifyTopupAttemptSummary
  canResolve: boolean
  busy: boolean
  onResolve: (resolution: 'credited' | 'failed') => void
}) {
  return (
    <TableRow>
      <TableCell className="font-medium">{attempt.meterHandle}</TableCell>
      <TableCell>{formatCentsAsUsd(attempt.priceCents)}</TableCell>
      <TableCell>{formatCredits(attempt.creditMicrousd)}</TableCell>
      <TableCell>
        <div className="flex flex-col gap-1">
          <Badge variant={attempt.escalatedAt ? 'destructive' : 'outline'}>
            {attempt.escalatedAt ? 'escalated' : attempt.status}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {attempt.attemptCount} attempt(s) · {new Date(attempt.createdAt).toLocaleString()}
          </span>
          {attempt.error && (
            <span className="text-xs text-muted-foreground">{attempt.error}</span>
          )}
        </div>
      </TableCell>
      <TableCell className="font-mono text-xs">{attempt.idempotencyKey}</TableCell>
      {canResolve && (
        <TableCell>
          <div className="flex gap-1">
            {/* Shopify billed it — pay the merchant what they bought. */}
            <Button size="sm" disabled={busy} onClick={() => onResolve('credited')}>
              Credit
            </Button>
            {/* Shopify never billed it — nothing is owed. */}
            <Button size="sm" variant="outline" disabled={busy} onClick={() => onResolve('failed')}>
              Write off
            </Button>
          </div>
        </TableCell>
      )}
    </TableRow>
  )
}
